import { useState } from "react";
import { ChevronDown } from "lucide-react";

// v1.0.0 Item 12 — Dedupe Merger Wrappers
// Three merger wrappers combine related panels into a single SectionPanel body
// with a tab switcher. No panel's internal logic or fetch is touched — the wrapper
// only decides which child to render based on the active tab.

function TabSwitcher({ tabs, active, onChange, testId }) {
  return (
    <div className="flex gap-1 p-1 bg-slate-100 rounded-xl mb-3" data-testid={testId}>
      {tabs.map((t) => (
        <button
          key={t.key}
          onClick={() => onChange(t.key)}
          className={`flex-1 px-3 py-1.5 rounded-lg text-[11px] font-bold transition ${
            active === t.key ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"
          }`}
          data-testid={`${testId}-${t.key}`}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

// ── PIPELINES — Workflow Pipeline + Rolling Pipeline Status + Discovery Analytics ──
export function PipelinesMergedPanel({ WorkflowPipelinePanel, RollingPipelineStatusPanel, DiscoveryAnalyticsPanel }) {
  const [active, setActive] = useState("workflow");
  return (
    <div data-testid="pipelines-merged">
      <TabSwitcher
        active={active}
        onChange={setActive}
        testId="pipelines-tabs"
        tabs={[
          { key: "workflow", label: "Workflow" },
          { key: "rolling", label: "Rolling Status" },
          { key: "discovery", label: "Discovery Analytics" },
        ]}
      />
      {active === "workflow" && <WorkflowPipelinePanel />}
      {active === "rolling" && <RollingPipelineStatusPanel />}
      {active === "discovery" && <DiscoveryAnalyticsPanel />}
    </div>
  );
}

// ── ACQUISITION — Discovery Channels + Submission Source Mix + Discovery Source Mix + Source ROI ──
export function AcquisitionMergedPanel({ DiscoveryChannelsContent, SubmissionSourceMixContent, DiscoverySourceMixContent, SourceROIPanel }) {
  const [active, setActive] = useState("channels");
  return (
    <div data-testid="acquisition-merged">
      <TabSwitcher
        active={active}
        onChange={setActive}
        testId="acquisition-tabs"
        tabs={[
          { key: "channels", label: "Channels" },
          { key: "submission-mix", label: "Submission Mix" },
          { key: "discovery-mix", label: "Discovery Mix" },
          { key: "roi", label: "Source ROI" },
        ]}
      />
      {active === "channels" && (DiscoveryChannelsContent ? <DiscoveryChannelsContent /> : <p className="text-xs text-slate-400 text-center py-4">Discovery Channels view</p>)}
      {active === "submission-mix" && (SubmissionSourceMixContent ? <SubmissionSourceMixContent /> : <p className="text-xs text-slate-400 text-center py-4">Submission Source Mix</p>)}
      {active === "discovery-mix" && (DiscoverySourceMixContent ? <DiscoverySourceMixContent /> : <p className="text-xs text-slate-400 text-center py-4">Discovery Source Mix</p>)}
      {active === "roi" && <SourceROIPanel />}
    </div>
  );
}

// ── DATA QUALITY — Data Completeness + Verification Checklist + Reverification Priority ──
export function DataQualityMergedPanel({ DataCompletenessPanel, VerificationChecklistContent, ReverificationPriorityContent }) {
  const [active, setActive] = useState("completeness");
  return (
    <div data-testid="data-quality-merged">
      <TabSwitcher
        active={active}
        onChange={setActive}
        testId="data-quality-tabs"
        tabs={[
          { key: "completeness", label: "Completeness" },
          { key: "verification", label: "Verification" },
          { key: "reverification", label: "Reverification" },
        ]}
      />
      {active === "completeness" && <DataCompletenessPanel />}
      {active === "verification" && (VerificationChecklistContent ? <VerificationChecklistContent /> : <p className="text-xs text-slate-400 text-center py-4">Verification checklist view</p>)}
      {active === "reverification" && (ReverificationPriorityContent ? <ReverificationPriorityContent /> : <p className="text-xs text-slate-400 text-center py-4">Reverification priority view</p>)}
    </div>
  );
}

// ── ADDRESS VERIFICATION — Address Verification primary + Melissa Drip secondary ──
// Address Verification lives in MelissaDripPanel component today (historic naming).
// Batch B presents MelissaDripPanel as the primary "Address Verification" view with
// a secondary tab. When Melissa activates later the labels can be swapped.
export function AddressVerificationMergedPanel({ MelissaDripPanel }) {
  const [active, setActive] = useState("primary");
  return (
    <div data-testid="address-verification-merged">
      <TabSwitcher
        active={active}
        onChange={setActive}
        testId="address-verification-tabs"
        tabs={[
          { key: "primary", label: "Address Verification (Primary)" },
          { key: "melissa", label: "Melissa Drip (Secondary)" },
        ]}
      />
      {active === "primary" && <MelissaDripPanel />}
      {active === "melissa" && (
        <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
          <p className="text-xs text-slate-600 mb-2">
            <strong>Melissa Drip</strong> is currently inactive. When activated it becomes the primary view and Address Verification moves to secondary.
          </p>
          <MelissaDripPanel />
        </div>
      )}
    </div>
  );
}
